package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.presentation.home.ClientVenueLocationFilter
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppSearchField
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import kotlin.math.abs
import kotlin.math.roundToInt
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_filter_apply
import top.clientapp.generated.resources.client_filter_close_content_description
import top.clientapp.generated.resources.client_filter_location
import top.clientapp.generated.resources.client_filter_location_around
import top.clientapp.generated.resources.client_filter_location_over_40
import top.clientapp.generated.resources.client_filter_location_within_40
import top.clientapp.generated.resources.client_filter_price_range
import top.clientapp.generated.resources.client_filter_rating
import top.clientapp.generated.resources.client_filter_remove_service_content_description
import top.clientapp.generated.resources.client_filter_service_search_placeholder
import top.clientapp.generated.resources.client_filter_services
import top.clientapp.generated.resources.client_filter_title

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ClientVenueFilterContent(
    locationFilter: ClientVenueLocationFilter,
    serviceSearchQuery: String,
    serviceSuggestions: List<String>,
    selectedServiceQueries: List<String>,
    priceBounds: IntRange,
    priceRange: IntRange,
    ratingRange: IntRange,
    onLocationClick: (ClientVenueLocationFilter) -> Unit,
    onServiceSearchChange: (String) -> Unit,
    onServiceSuggestionClick: (String) -> Unit,
    onSelectedServiceRemove: (String) -> Unit,
    onPriceRangeChange: (IntRange) -> Unit,
    onRatingRangeChange: (IntRange) -> Unit,
    onBackClick: () -> Unit,
    onApplyClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .padding(horizontal = 24.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clickable(onClick = onBackClick),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.ArrowLeft,
                    contentDescription = stringResource(Res.string.client_filter_close_content_description),
                    tint = MineShaft950,
                    modifier = Modifier.size(20.dp)
                )
            }

            Text(
                text = stringResource(Res.string.client_filter_title),
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 14.sp,
                    lineHeight = 20.sp,
                    fontWeight = FontWeight.Medium
                )
            )
        }

        Spacer(modifier = Modifier.height(16.dp))

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(22.dp)
        ) {
            FilterSection(title = stringResource(Res.string.client_filter_location)) {
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    FilterChip(
                        text = stringResource(Res.string.client_filter_location_around),
                        selected = locationFilter == ClientVenueLocationFilter.Around,
                        onClick = { onLocationClick(ClientVenueLocationFilter.Around) }
                    )
                    FilterChip(
                        text = stringResource(Res.string.client_filter_location_within_40),
                        selected = locationFilter == ClientVenueLocationFilter.Within40Km,
                        onClick = { onLocationClick(ClientVenueLocationFilter.Within40Km) }
                    )
                    FilterChip(
                        text = stringResource(Res.string.client_filter_location_over_40),
                        selected = locationFilter == ClientVenueLocationFilter.Over40Km,
                        onClick = { onLocationClick(ClientVenueLocationFilter.Over40Km) }
                    )
                }
            }

            FilterSection(title = stringResource(Res.string.client_filter_services)) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    AppSearchField(
                        value = serviceSearchQuery,
                        onValueChange = onServiceSearchChange,
                        placeholder = stringResource(Res.string.client_filter_service_search_placeholder),
                        modifier = Modifier.fillMaxWidth()
                    )

                    if (selectedServiceQueries.isNotEmpty()) {
                        FlowRow(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            selectedServiceQueries.forEach { service ->
                                SelectedServiceChip(
                                    text = service,
                                    onRemoveClick = { onSelectedServiceRemove(service) }
                                )
                            }
                        }
                    }

                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        serviceSuggestions.take(ServiceSuggestionLimit).forEach { service ->
                            FilterChip(
                                text = service,
                                selected = false,
                                onClick = { onServiceSuggestionClick(service) }
                            )
                        }
                    }
                }
            }

            FilterRangeSelector(
                title = stringResource(Res.string.client_filter_price_range),
                value = priceRange,
                bounds = priceBounds,
                valueText = "${priceRange.first} - ${priceRange.last} грн",
                onValueChange = onPriceRangeChange
            )

            FilterRangeSelector(
                title = stringResource(Res.string.client_filter_rating),
                value = ratingRange,
                bounds = 0..10,
                valueText = "${ratingRange.first}-${ratingRange.last}",
                onValueChange = onRatingRangeChange
            )
        }

        Spacer(modifier = Modifier.height(24.dp))

        AppButton(
            text = stringResource(Res.string.client_filter_apply),
            onClick = onApplyClick,
            type = ButtonType.Black,
            modifier = Modifier.fillMaxWidth()
        )

        Spacer(modifier = Modifier.height(16.dp))
    }
}

@Composable
private fun SelectedServiceChip(
    text: String,
    onRemoveClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .height(42.dp)
            .clip(RoundedCornerShape(100.dp))
            .background(MineShaft950)
            .clickable(onClick = onRemoveClick)
            .padding(start = 16.dp, end = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = text,
            style = BodyMedium.copy(
                color = White,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                fontWeight = FontWeight.Normal
            )
        )

        Icon(
            painter = AppIcons.Close,
            contentDescription = stringResource(Res.string.client_filter_remove_service_content_description),
            tint = White,
            modifier = Modifier.size(14.dp)
        )
    }
}

@Composable
private fun FilterSection(
    title: String,
    content: @Composable () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text(
            text = title,
            style = BodyMedium.copy(
                color = MineShaft900,
                fontSize = 18.sp,
                lineHeight = 27.sp,
                letterSpacing = (-0.44).sp,
                fontWeight = FontWeight.Medium
            )
        )
        content()
    }
}

@Composable
private fun FilterChip(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .height(42.dp)
            .clip(RoundedCornerShape(100.dp))
            .background(if (selected) MineShaft950 else MineShaft50)
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = text,
            style = BodyMedium.copy(
                color = if (selected) White else MineShaft900,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                fontWeight = FontWeight.Normal,
                textAlign = TextAlign.Center
            )
        )
    }
}

@Composable
private fun FilterRangeSelector(
    title: String,
    value: IntRange,
    bounds: IntRange,
    valueText: String,
    onValueChange: (IntRange) -> Unit,
    modifier: Modifier = Modifier
) {
    val safeBounds = if (bounds.first < bounds.last) bounds else bounds.first..(bounds.first + 1)
    val safeValue = value.coerceIn(safeBounds)

    Column(modifier = modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = title,
                style = BodyMedium.copy(
                    color = MineShaft900,
                    fontSize = 18.sp,
                    lineHeight = 27.sp,
                    letterSpacing = (-0.44).sp,
                    fontWeight = FontWeight.Medium
                ),
                modifier = Modifier.weight(1f)
            )

            Text(
                text = valueText,
                style = BodyMedium.copy(
                    color = MineShaft500,
                    fontSize = 16.sp,
                    lineHeight = 24.sp,
                    letterSpacing = (-0.31).sp,
                    fontWeight = FontWeight.Medium
                )
            )
        }

        Spacer(modifier = Modifier.height(12.dp))

        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.dp)
                .pointerInput(safeValue, safeBounds) {
                    detectTapGestures { offset ->
                        val tappedValue = safeBounds.valueAt(offset.x / size.width)
                        val updateStart = abs(tappedValue - safeValue.first) <= abs(tappedValue - safeValue.last)
                        val nextValue = if (updateStart) {
                            tappedValue.coerceIn(safeBounds.first, safeValue.last - 1)..safeValue.last
                        } else {
                            safeValue.first..tappedValue.coerceIn(safeValue.first + 1, safeBounds.last)
                        }
                        onValueChange(nextValue)
                    }
                }
        ) {
            val startFraction = safeBounds.fractionOf(safeValue.first)
            val endFraction = safeBounds.fractionOf(safeValue.last)

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(4.dp)
                    .align(Alignment.Center)
                    .clip(RoundedCornerShape(100.dp))
                    .background(MineShaft100)
            )

            Box(
                modifier = Modifier
                    .offset(x = maxWidth * startFraction)
                    .height(4.dp)
                    .fillMaxWidth(endFraction - startFraction)
                    .align(Alignment.CenterStart)
                    .clip(RoundedCornerShape(100.dp))
                    .background(Primary600)
            )

            RangeHandle(
                modifier = Modifier
                    .offset(x = handleOffset(maxWidth, startFraction))
                    .align(Alignment.CenterStart)
            )

            RangeHandle(
                modifier = Modifier
                    .offset(x = handleOffset(maxWidth, endFraction))
                    .align(Alignment.CenterStart)
            )
        }
    }
}

@Composable
private fun RangeHandle(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .size(RangeHandleSize)
            .clip(RoundedCornerShape(14.dp))
            .background(Primary600),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            painter = AppIcons.ArrowDown,
            contentDescription = null,
            tint = White,
            modifier = Modifier.size(16.dp)
        )
    }
}

private fun IntRange.fractionOf(value: Int): Float {
    val span = (last - first).coerceAtLeast(1)
    return ((value - first).toFloat() / span).coerceIn(0f, 1f)
}

private fun IntRange.valueAt(fraction: Float): Int {
    val span = (last - first).coerceAtLeast(1)
    return (first + span * fraction.coerceIn(0f, 1f)).roundToInt().coerceIn(first, last)
}

private fun IntRange.coerceIn(bounds: IntRange): IntRange {
    val start = first.coerceIn(bounds.first, bounds.last - 1)
    val end = last.coerceIn(start + 1, bounds.last)
    return start..end
}

private fun handleOffset(width: Dp, fraction: Float): Dp {
    val maxOffset = width - RangeHandleSize
    return (width * fraction - RangeHandleSize / 2).coerceIn(0.dp, maxOffset)
}

private val RangeHandleSize = 42.dp
private const val ServiceSuggestionLimit = 20
